/**
 * Check if we're running in a browser environment
 */
const isBrowser = typeof globalThis !== 'undefined' &&
    typeof globalThis.window !== 'undefined' &&
    typeof globalThis.document !== 'undefined';
/**
 * Format log arguments for structured output
 */
function formatArgs(args) {
    if (args.length === 0)
        return '';
    return args
        .map((arg) => {
        if (arg instanceof Error) {
            return `Error: ${arg.message}\n${arg.stack || ''}`;
        }
        if (typeof arg === 'object') {
            try {
                return JSON.stringify(arg, null, 2);
            }
            catch {
                return String(arg);
            }
        }
        return String(arg);
    })
        .join(' ');
}
/**
 * Browser-compatible logger implementation
 * Uses console with structured output
 */
class BrowserLogger {
    name;
    constructor(name) {
        this.name = name;
    }
    format(level, msg, args) {
        const timestamp = new Date().toISOString();
        const prefix = `[${timestamp}] [${level.toUpperCase()}] [${this.name}]`;
        const formatted = formatArgs(args);
        if (formatted) {
            // eslint-disable-next-line no-console
            console.log(`${prefix} ${msg}`, formatted);
        }
        else {
            // eslint-disable-next-line no-console
            console.log(`${prefix} ${msg}`);
        }
    }
    info(msg, ...args) {
        this.format('info', msg, args);
    }
    warn(msg, ...args) {
        this.format('warn', msg, args);
    }
    error(msg, ...args) {
        this.format('error', msg, args);
    }
    debug(msg, ...args) {
        this.format('debug', msg, args);
    }
}
/**
 * Node.js compatible logger implementation
 * Uses simple formatting for terminal output
 */
class NodeLogger {
    name;
    constructor(name) {
        this.name = name;
    }
    format(level, msg, args) {
        const timestamp = new Date().toISOString();
        const prefix = `[${timestamp}] [${level.toUpperCase()}] [${this.name}]`;
        const formatted = formatArgs(args);
        return formatted ? `${prefix} ${msg} ${formatted}` : `${prefix} ${msg}`;
    }
    info(msg, ...args) {
        // eslint-disable-next-line no-console
        console.log(this.format('info', msg, args));
    }
    warn(msg, ...args) {
        // eslint-disable-next-line no-console
        console.warn(this.format('warn', msg, args));
    }
    error(msg, ...args) {
        // eslint-disable-next-line no-console
        console.error(this.format('error', msg, args));
    }
    debug(msg, ...args) {
        if (process.env.NODE_ENV === 'development' || process.env.DEBUG) {
            // eslint-disable-next-line no-console
            console.debug(this.format('debug', msg, args));
        }
    }
}
/**
 * Create a logger instance with the given name
 * Returns a browser-compatible logger in browser environments
 * Returns a Node.js logger in Node environments
 *
 * @param name - The name/identifier for this logger instance
 * @returns A Logger instance
 */
export function createLogger(name) {
    return isBrowser ? new BrowserLogger(name) : new NodeLogger(name);
}
//# sourceMappingURL=logger.js.map